import {
  useMutation,
  useQueryClient,
  useQuery,
  QueryClient,
} from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { driverApi } from '../services/driverApi';
import { isVideoMimeType } from '../utils/mediaUtils';

export const prefetchVehicleImages = (
  queryClient: QueryClient,
  token: string,
  vehicleId: string | undefined
): Promise<void> => {
  if (!vehicleId) return Promise.resolve();
  return queryClient.prefetchQuery({
    queryKey: ['vehicleImages', vehicleId],
    queryFn: async () => {
      driverApi.setAuthToken(token);
      return driverApi.getVehicleImages(vehicleId);
    },
  });
};

export function useVehicleImages(vehicleId?: string) {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useHybridAuth();

  const {
    data: vehicleImages = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['vehicleImages', vehicleId],
    queryFn: async () => {
      if (!vehicleId) return [];
      const token = await getAccessTokenSilently();
      driverApi.setAuthToken(token);
      return driverApi.getVehicleImages(vehicleId);
    },
    enabled: !!vehicleId,
  });

  const uploadImage = useMutation({
    mutationFn: async ({
      vehicleId,
      file,
      documentType,
      onUploadProgress,
    }: {
      vehicleId: string;
      file: File;
      documentType?: string;
      onUploadProgress?: (progress: number) => void;
    }) => {
      const token = await getAccessTokenSilently();
      driverApi.setAuthToken(token);
      const isVideo = isVideoMimeType(file.type);
      if (isVideo) {
        return driverApi.uploadVehicleMedia({
          vehicleId,
          file,
          documentType,
          onUploadProgress,
        });
      } else {
        return driverApi.uploadVehicleImage({
          vehicleId,
          file,
          documentType,
          onUploadProgress,
        });
      }
    },
    onMutate: async ({ file }) => {
      // Show the local file immediately as a preview while upload is in progress
      const blobUrl = URL.createObjectURL(file);
      queryClient.setQueryData<string[]>(
        ['vehicleImages', vehicleId],
        (old = []) => [...old, blobUrl]
      );
      return { blobUrl };
    },
    onSuccess: (_data, _variables, context) => {
      // Invalidate so the real server URL replaces the blob URL
      queryClient.invalidateQueries({ queryKey: ['vehicleImages', vehicleId] });
      queryClient.invalidateQueries({ queryKey: ['driverCall'] });
      // Revoke blob URL after refetch has had time to settle
      if (context?.blobUrl)
        setTimeout(() => URL.revokeObjectURL(context.blobUrl), 5000);
    },
    onError: (_error, _variables, context) => {
      // Roll back the optimistic blob URL
      if (context?.blobUrl) {
        queryClient.setQueryData<string[]>(
          ['vehicleImages', vehicleId],
          (old = []) => old.filter((url) => url !== context.blobUrl)
        );
        URL.revokeObjectURL(context.blobUrl);
      }
    },
  });

  const deleteImage = useMutation({
    mutationFn: async ({
      vehicleId,
      imageUrl,
    }: {
      vehicleId: string;
      imageUrl: string;
    }) => {
      const token = await getAccessTokenSilently();
      driverApi.setAuthToken(token);
      return driverApi.deleteVehicleImage(vehicleId, imageUrl);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicleImages', vehicleId] });
      queryClient.invalidateQueries({ queryKey: ['driverCall'] });
    },
  });

  return {
    vehicleImages,
    isLoading,
    error,
    uploadImage,
    deleteImage,
  };
}
